
import React, { useEffect, useState } from "react";
import { Table, Spin, Button, Modal, Input } from "antd";
import LoadingOverlay from "../shared/LoadingOverlay";
import Toast from "../shared/Toast";
import { runMilestoneRules, saveMilestoneOverride } from "../../services/rulesApi";
import { info, error as logAppInsightError } from '../../utils/logger';
import { logError } from "../../utils/logError";
import { getAccessToken } from "../../services/authApi";
import { fieldReader, fieldWriter, createTask } from "../../services/loanApi";


const MilestoneValidationUpdated = () => {

    const [historyData, setHistoryData] = useState([]);
    const [ruleReviewedDate, setRuleReviewedDate] = useState("");
    const [isInitialized, setIsInitialized] = useState(false);

    // State for override modal
    const [overrideModalVisible, setOverrideModalVisible] = useState(false);
    const [overrideReason, setOverrideReason] = useState("");
    const [overrideLoading, setOverrideLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState("");
    const [toastVisible, setToastVisible] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [toastType, setToastType] = useState("success");
    const [auditData, setAuditData] = useState([]);
    const [loanId, setLoanId] = useState("");
    const [milestoneName, setMilestoneName] = useState("");
    const [channelName, setChannelName] = useState("");
    const [userId, setUserId] = useState("");
    const [authCode, setAuthCode] = useState("");
    const [allowedPersona, setAllowedPersona] = useState(false);

    useEffect(() => {
        if (window.elli && window.elli.script && window.elli.script.guest) {
            window.elli.script.guest.create();
            initializeAuthObject();

        } else {
            logAppInsightError("elli.script.guest not available");
            console.warn("elli.script.guest is not available.");
        }
    }, []);


    const initializeAuthObject = async () => {
        info("Initializing MilestoneValidationUpdated", {
            loanId,
            milestoneName,
            channelName
        });
        try {
            setIsInitialized(false);

            console.log("Getting loan object...");
            const loanObject = await window.elli.script.getObject("loan");
            console.log("Loan object received", loanObject);
            const loan = await loanObject.all();
            console.log("Loan data received", loan);
            setLoanId(loan?.loan?.id || "");
            await loadRuleReviewedDate(loan.loan.id);

            setMilestoneName(loan?.loan?.milestoneNextName || "");
            const isDelegated = loan?.loan?.tpo?.underwritingDelegated;
            console.log("Is Delegated:", isDelegated); 
            let channel = loan?.loan?.channel || "";
            if (channel === "Banked - Wholesale") {
                channel = "Wholesale";
            }
            else if (channel === "Correspondent" && isDelegated) {
                channel = "Correspondent Delegate";
            } else if (channel === "Correspondent" && !isDelegated) {
                channel = "Correspondent NonDelegate";
            }
            console.log("Channel determined as:", channel);
            setChannelName(channel);

            console.log("Getting auth object...");
            const tpoAuthObject = await window.elli.script.getObject("auth");
            console.log("Auth object received", tpoAuthObject);
            const code = await tpoAuthObject.createAuthCode();
            console.log("Auth code received", code);
            setAuthCode(code || "");

            console.log("Getting user info...");

            const user = await tpoAuthObject.getUser();
            console.log("User info received", user);
            let userFullName = "";
            if (user) {
                userFullName = `${user['firstName']} ${user['lastName']}`;
                setUserId(userFullName);
                const allowed = hasAllowedPersona(user);
                setAllowedPersona(allowed);
            }


            // Only call API if all required values are present
            if (loan?.loan?.id && loan?.loan?.milestoneNextName && userFullName && channel) {
                const apiResults = await runMilestoneRules({
                    channelName: channel,
                    milestoneName: loan.loan.milestoneNextName,
                    loanId: loan.loan.id,
                    executedBy: userFullName,
                });
                
                // Transform API result to table format for Rule Name and Status
                const dataWithKeys = Array.isArray(apiResults)
                    ? apiResults.map((item, idx) => {
                        let status = "";
                        if (item.isPass) {
                            status = "Passed";
                        } else if (item.errorSeverity && item.errorSeverity.toLowerCase() === "fatal") {
                            status = "Fatal";
                        } else if (item.errorSeverity && item.errorSeverity.toLowerCase() === "warning") {
                            status = "Warning";
                        }
                        return {
                            key: idx + 1,
                            ruleName: item.ruleName || "-",
                            status: status,
                            type: status,
                            ruleId: item.ruleId
                        };
                    })
                    : [];
                setAuditData(dataWithKeys);
                info("Milestone rules executed successfully", {
                    loanId: loan.loan.id,
                    milestone: loan.loan.milestoneNextName,
                    totalRules: dataWithKeys.length
                });
                await loadHistory(loan?.loan?.id);

            } else {
                setToastMessage("Missing context: loan, milestone, user, or auth code.");
                setToastType("error");
                setToastVisible(true);
            }
        } catch (err) {
            logAppInsightError("Error initializing auth object", {
                message: err?.message,
                stack: err?.stack,
                loanId
            });
            logError(err);

            if (err && err.stack) {
                console.error("Stack trace:", err.stack);
            }
            setToastMessage("Failed to initialize auth object. " + (err && err.message ? err.message : ""));
            setToastType("error");
            setToastVisible(true);
        } finally {
            setLoading(false);
            setLoadingText("");
            setIsInitialized(true);
        }
    }
    const loadRuleReviewedDate = async (loanIdValue) => {
        try {
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();
            const token = await getAccessToken(authCode);

            if (!token) return;

            const fieldVal = await fieldReader(token, ["CX.MS.RULEREVIEWED.DATE"], loanIdValue);

            const dateValue = fieldVal?.["CX.MS.RULEREVIEWED.DATE"] || "";

            setRuleReviewedDate(dateValue);
        } catch (err) {
            logAppInsightError("Failed to load rule reviewed date", {
                loanId: loanIdValue,
                message: err?.message
            });
            logError(err);
        }
    };


    // Helper to format date as mm/dd/yyyy
    const getCurrentDateMMDDYYYY = () => {
        const now = new Date();
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        const yyyy = now.getFullYear();
        return `${yyyy}-${mm}-${dd}`;
    };

    // Helper to update override history field (append mode)
    const updateOverrideHistory = async ({ reviewedDate, reviewedBy, overridden, reason }) => {
        info("Updating override history", {
            loanId,
            milestoneName,
            reviewedBy,
            overridden
        });

        const historyObj = {
            milestone: milestoneName,
            reviewedDate,
            reviewedBy,
            overridden,
            reason
        };
        const tpoAuthObject = await window.elli.script.getObject("auth");
        const authCode = await tpoAuthObject.createAuthCode();
        const token = await getAccessToken(authCode);
        const dateStr = getCurrentDateMMDDYYYY();

        if (token) {
            // Read current history
            let currentHistory = [];
            try {
                const fieldVal = await fieldReader(token, ["CX.MS.OVERRIDE.HISTORY"], loanId);
                console.log("Current override history field value:", fieldVal[0]?.value);
                currentHistory = fieldVal['CX.MS.OVERRIDE.HISTORY'] || "";
            } catch (e) {
                currentHistory = "";
            }

            const newEntries = JSON.stringify(historyObj);
            currentHistory = currentHistory
                ? JSON.parse(currentHistory) + "\n" + newEntries
                : newEntries;
            const fieldData = [
                {
                    id: "CX.MS.OVERRIDE.HISTORY",
                    value: JSON.stringify(currentHistory)
                },
                {
                    id: "CX.MS.RULEREVIEWED.DATE",
                    value: dateStr
                }
            ];
            await fieldWriter(token, loanId, fieldData);
            const ruleIds = auditData
                .map(r => r.ruleId)
                .filter(Boolean)
                .join(",");

            // UPDATE TO SQL HISTORY TABLE API CALL
            info("Saving override to SQL", {
                loanId,
                milestoneName,
                ruleIds
            });
            try {
                const apiResults = await saveMilestoneOverride({
                    milestoneName: milestoneName,
                    ruleId: ruleIds,
                    loanId: loanId,
                    overrideReason: reason,
                    detailsJson: newEntries,
                    performedBy: reviewedBy,
                    performedAt: reviewedDate
                });
            } catch (err) {
                logAppInsightError("Failed saving override to SQL", {
                    loanId,
                    milestoneName,
                    ruleIds,
                    message: err?.message
                });
                logError(err);
                throw err;
            }

            info("Override saved successfully", {
                loanId,
                ruleIds
            });

            await syncHostLoanWorkspace();
            // // Add a short delay to allow host to process milestone completion
            await new Promise(resolve => setTimeout(resolve, 1000)); // 1 second delay
            // // Refresh data after update
            setRuleReviewedDate(dateStr);
            await loadHistory(loanId);
        }
    };


    async function syncHostLoanWorkspace() {
        try {
            const loanObj = await window.elli.script.getObject("loan");
            await loanObj.merge();
            await loanObj.calculate();
        } catch (e) {
            console.warn("Host loan merge/calc failed (workspace may stay stale until refresh):", e);
        }
    }

    // Handler for Rules Reviewed button
    const handleRulesReviewed = async (customReason = "Rules viewed") => {
        info("Rules reviewed clicked", {
            loanId,
            milestoneName,
            userId
        });
        try {
            setLoading(true);
            setLoadingText("Updating rule reviewed date...");

            const dateStr = getCurrentDateMMDDYYYY();

            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();
            const token = await getAccessToken(authCode);

            if (token) {
                const fieldData = [
                    {
                        id: "CX.MS.RULEREVIEWED.DATE",
                        value: dateStr
                    }
                ];
                await fieldWriter(token, loanId, fieldData);
            }

            await updateOverrideHistory({
                reviewedDate: dateStr,
                reviewedBy: userId,
                overridden: false,
                reason: customReason
            });

            setRuleReviewedDate(dateStr); // update UI immediately

            setToastMessage(customReason.toLocaleLowerCase().includes("no rules") ? "Acknowledged successfully." : "Rules reviewed successfully.");
            setToastType("success");
            setToastVisible(true);

        } catch (err) {
            logAppInsightError("Failed to update rule reviewed date", {
                loanId,
                milestoneName,
                message: err?.message
            });
            logError(err);

            setToastMessage("Failed to update rule reviewed date.");
            setToastType("error");
            setToastVisible(true);
        } finally {
            setLoading(false);
            setLoadingText("");
        }
    };


    // Handler for Override button
    const handleOverride = () => {
        setOverrideModalVisible(true);
        setOverrideReason("");
    };

    // Handler for modal OK
    const handleOverrideOk = async () => {
        info("Override submitted", {
            loanId,
            milestoneName,
            ruleIds: auditData?.map(r => r.ruleId).join(","),
            reason: overrideReason
        });
        setOverrideLoading(true);
        try {
            const dateStr = getCurrentDateMMDDYYYY();
            await updateOverrideHistory({
                reviewedDate: dateStr,
                reviewedBy: userId,
                overridden: true,
                reason: overrideReason
            });
            setToastMessage("Milestone overridden successfully.");
            setToastType("success");
            setToastVisible(true);
            setOverrideModalVisible(false);
        } catch (err) {
            logAppInsightError("Override submission failed", {
                loanId,
                milestoneName,
                message: err?.message
            });
            logError(err);

            setToastMessage("Failed to override milestone.");
            setToastType("error");
            setToastVisible(true);
        } finally {
            setOverrideLoading(false);
        }
    };

    // Handler for modal Cancel
    const handleOverrideCancel = () => {
        setOverrideModalVisible(false);
        setOverrideReason("");
    };

    const columns = [
        {
            title: "Rule Name",
            dataIndex: "ruleName",
            key: "ruleName",
            width: "60%",
            render: (text) =>
                text ? (
                    <span className="text-blue-600 font-medium">
                        {text}
                    </span>
                ) : (
                    "-"
                )
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: "40%",
            render: (text) => {
                let color = "#d1d5db"; // gray-300 default
                let fontColor = "#111";
                if (text === "Passed") {
                    color = "#22c55e"; // green-500
                    fontColor = "#fff";
                } else if (text === "Warning") {
                    color = "#facc15"; // yellow-400
                    fontColor = "#000";
                } else if (text === "Fatal") {
                    color = "#dc2626"; // red-600
                    fontColor = "#fff";
                }
                return (
                    <span
                        style={{
                            display: "inline-block",
                            minWidth: 80,
                            padding: "2px 16px",
                            borderRadius: 8,
                            background: color,
                            color: fontColor,
                            fontWeight: 700,
                            textAlign: "center"
                        }}
                    >
                        {text}
                    </span>
                );
            }
        }
    ];


    const historyColumns = [
        {
            title: "Milestone",
            dataIndex: "milestone",
            key: "milestone"
        },
        {
            title: "Reviewed Date",
            dataIndex: "reviewedDate",
            key: "reviewedDate"
        },
        {
            title: "Reviewed By",
            dataIndex: "reviewedBy",
            key: "reviewedBy"
        },
        {
            title: "Overridden",
            dataIndex: "overridden",
            key: "overridden"
        },
        {
            title: "Reason",
            dataIndex: "reason",
            key: "reason"
        }
    ];

    const hasAuditRecords = auditData.length > 0;
    const hasFatalError = auditData.some(row => row.type === "Fatal");
    const isRuleAlreadyReviewed = ruleReviewedDate !== "//" && ruleReviewedDate !== "";

    // Button conditions
    const showRulesReviewed =
        hasAuditRecords && !hasFatalError && !isRuleAlreadyReviewed;

    const showOverride =
        allowedPersona && hasAuditRecords && hasFatalError && !isRuleAlreadyReviewed;

    const showAcknowledge = !hasAuditRecords;
    // Custom sort order: Fatal, Warning, Passed
    const statusOrder = { Fatal: 1, Warning: 2, Passed: 3 };
    const sortedAuditData = [...auditData].sort((a, b) => {
        return (statusOrder[a.status] || 99) - (statusOrder[b.status] || 99);
    });




    const hasAllowedPersona = (user) => {
        const personas = Array.isArray(user?.personas) ? user.personas : [];
        const names = personas
            .map((p) => String(p?.entityName || "").trim().toLowerCase())
            .filter(Boolean);

        return names.includes("account manager");
    };

    const loadHistory = async (loanIdValue) => {
        try {
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();
            const token = await getAccessToken(authCode);
            if (!token) return;

            const fieldVal = await fieldReader(token, ["CX.MS.OVERRIDE.HISTORY"], loanIdValue);
            let historyRaw = fieldVal?.["CX.MS.OVERRIDE.HISTORY"];

            if (!historyRaw) {
                setHistoryData([]);
                return;
            }

            let cleanString = historyRaw;

            try {
                cleanString = JSON.parse(historyRaw);
            } catch (e) {
                cleanString = historyRaw;
            }
            const lines = cleanString.split("\n").filter(Boolean);

            const formatted = lines.map((line, index) => {
                try {
                    const parsedLine = JSON.parse(line);

                    return {
                        key: index + 1,
                        milestone: parsedLine.milestone || "-",
                        reviewedDate: parsedLine.reviewedDate || "-",
                        reviewedBy: parsedLine.reviewedBy || "-",
                        overridden: parsedLine.overridden ? "Yes" : "No",
                        reason: parsedLine.reason || "-"
                    };
                } catch (err) {
                    console.error("Still failed parsing line:", line);
                    return null;
                }
            }).filter(Boolean);

            setHistoryData(formatted.reverse()); // newest on top

        } catch (err) {
            logAppInsightError("Failed to load override history", {
                loanId: loanIdValue,
                message: err?.message
            });
            logError(err);

            setHistoryData([]);
        }
    };


    return (
        <div className="space-y-10 p-6">
            <div className="bg-white rounded-lg p-4 space-y-6 shadow max-w-7xl mx-auto">
                <LoadingOverlay loading={loading} text={loadingText} />

                <div className="flex items-center justify-between mb-4">
                    <div className="text-xl font-semibold text-gray-800 border-b pb-2">
                        Milestone Rules
                    </div>
                    <div className="space-x-2">
                        {isInitialized && (
                            <>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 w-full">
                                    {/* CASE 1: Rules exist and have NO FATAL */}
                                    {/* CASE 2: Rules exist and have FATAL, allowedPersona can override */}
                                    {showRulesReviewed && (
                                        <Button
                                            type="primary"
                                            className="w-full sm:w-auto"
                                            onClick={() => handleRulesReviewed("Rules viewed")}
                                            disabled={isRuleAlreadyReviewed || hasFatalError}
                                            style={{
                                                backgroundColor: "#006aa9",
                                                borderColor: "#006aa9",
                                                color: "whitesmoke"
                                            }}
                                        >
                                            Rules Reviewed
                                        </Button>
                                    )}


                                    {showOverride && (
                                        <Button
                                            type="default"
                                            className="w-full sm:w-auto"
                                            onClick={handleOverride}
                                            disabled={isRuleAlreadyReviewed}
                                            style={{
                                                backgroundColor: isRuleAlreadyReviewed ? "#d1d5db" : "#006aa9", // gray if disabled
                                                borderColor: isRuleAlreadyReviewed ? "#d1d5db" : "#006aa9",
                                                color: isRuleAlreadyReviewed ? "#888" : "whitesmoke"
                                            }}
                                        >
                                            Override
                                        </Button>
                                    )}
                                    
                                {(!isRuleAlreadyReviewed && hasAuditRecords && hasFatalError) && (
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 w-full">
                                        <Button type="primary" className="w-full sm:w-auto" disabled>
                                            Rules Reviewed
                                        </Button>
                                    </div>
                                )}
                                </div>

                                {/* CASE 3: No rules */}
                                {/* CASE 4: Rules already reviewed, show disabled button with date */}
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 w-full">
                                    {showAcknowledge && (
                                        <Button
                                            type="primary"
                                            className="w-full sm:w-auto"
                                            onClick={() => handleRulesReviewed("No Rules to be reviewed")}
                                            disabled={isRuleAlreadyReviewed}
                                            style={{
                                                backgroundColor: isRuleAlreadyReviewed ? "#d1d5db" : "#006aa9",
                                                borderColor: isRuleAlreadyReviewed ? "#d1d5db" : "#006aa9",
                                                color: isRuleAlreadyReviewed ? "#888" : "whitesmoke",
                                            }}
                                        >
                                            Acknowledge
                                        </Button>
                                    )}

                                    {isRuleAlreadyReviewed && (
                                        <Button
                                            type="primary"
                                            className="w-full sm:w-auto"
                                            disabled
                                        >
                                            Rules Reviewed/Overridden on ({ruleReviewedDate})
                                        </Button>
                                    )}
                                </div>

                                <Modal
                                    title="Override Reason"
                                    open={overrideModalVisible}
                                    onOk={handleOverrideOk}
                                    onCancel={handleOverrideCancel}
                                    confirmLoading={overrideLoading}
                                    okText="Submit"
                                    cancelText="Cancel"
                                    okButtonProps={{
                                        style: {
                                            backgroundColor: "#006aa9",
                                            borderColor: "#006aa9",
                                            color: "whitesmoke"
                                        }
                                    }}
                                >
                                    <div className="mb-2">Please provide a reason for override:</div>
                                    <Input.TextArea
                                        rows={4}
                                        value={overrideReason}
                                        onChange={e => setOverrideReason(e.target.value)}
                                        placeholder="Enter reason for override"
                                    />
                                </Modal>
                            </>
                        )}
                    </div>
                </div>

                {/* Only show content after initialization */}
                {!isInitialized ? (
                    <div className="flex justify-center items-center h-40">
                        <Spin size="large" />
                    </div>
                ) : loading ? (
                    <div className="flex justify-center items-center h-40">
                        <Spin size="large" />
                    </div>
                ) : auditData.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-40 space-y-4">
                        <div className="text-lg font-semibold text-gray-600">{(ruleReviewedDate !== '//' && ruleReviewedDate !== "") ? "No rules found." : "No rules found. Please proceed to click 'Acknowledge' to complete the milestone"}</div>
                    </div>
                ) : (
                    <Table
                        size="middle"
                        bordered
                        columns={columns}
                        dataSource={sortedAuditData}
                        pagination={false}
                    />
                )}

                <Toast
                    visible={toastVisible}
                    type={toastType}
                    message={toastMessage}
                    onClose={() => setToastVisible(false)}
                />

                {historyData.length > 0 && (
                    <div className="mt-8">
                        <div className="text-lg font-semibold text-gray-800 mb-4">
                            Override History
                        </div>
                        <Table
                            size="middle"
                            bordered
                            columns={historyColumns}
                            dataSource={historyData}
                            pagination={false}
                            scroll={{ y: 300 }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default MilestoneValidationUpdated;